import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import { useMyGames } from "@seabrookstudios/pitch2table";
import { useSnackbar } from "notistack";
import { findGameByNestiFyzId } from "./findGameByNestifyzId";
import { NestiFyzGamesListItem } from "./NestifyzGamesListItem";
import CardHeader from "@mui/material/CardHeader";

/**
 * @param {object} props
 * @param {import("../nestifyz/parseExport").NestiFyzGame[]} props.games
 */
export const NestiFyzGamesList = ({ games }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: userGames = [], isLoading, refetch } = useMyGames();
  const [importAllIndex, setImportAllIndex] = useState(/** @type {number|null} */ (null));

  const importableIndices = useMemo(
    () =>
      games
        .map((game, index) => ({ game, index }))
        .filter(({ game }) => game.id && !findGameByNestiFyzId(userGames, game.id))
        .map(({ index }) => index),
    [games, userGames]
  );

  const importAllActive = importAllIndex !== null;

  const startImportAll = () => {
    if (importableIndices.length === 0) {
      return;
    }

    setImportAllIndex(importableIndices[0]);
  };

  const advanceImportAll = () => {
    if (importAllIndex === null) {
      return;
    }

    const currentPosition = importableIndices.indexOf(importAllIndex);
    const nextIndex = importableIndices[currentPosition + 1];

    if (nextIndex === undefined) {
      setImportAllIndex(null);
      refetch();
      return;
    }

    setImportAllIndex(nextIndex);
  };

  const handleBatchImportError = () => {
    enqueueSnackbar("Import all stopped after a game failed to import.", { variant: "error" });
    setImportAllIndex(null);
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (games.length === 0) {
    return null;
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title={`Games (${games.length})`}
        action={
          <Button
            variant="outlined"
            size="small"
            disabled={importableIndices.length === 0 || importAllActive}
            onClick={startImportAll}
          >
            Import all
          </Button>
        }
      />
      <CardContent>
        <List disablePadding>
          {games.map((game, index) => (
            <NestiFyzGamesListItem
              key={game.id || game.title || index}
              game={game}
              matchedGame={findGameByNestiFyzId(userGames, game.id)}
              batchImportActive={importAllIndex === index}
              onBatchImportComplete={advanceImportAll}
              onBatchImportError={handleBatchImportError}
            />
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
